'use client';

import { Box, Grid, HStack, Link, Portal, SimpleGrid, Table, Text, VStack } from '@chakra-ui/react';
import {
  AlertTriangle,
  ArrowRight,
  BarChart3,
  Bot,
  ChevronDown,
  ChevronLeft,
  Cloud,
  ExternalLink,
  Eye,
  FileSearch,
  Globe2,
  MousePointerClick,
  Search,
  Sparkles,
} from 'lucide-react';
import NextLink from 'next/link';
import React, { useEffect, useMemo, useState } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useAuth } from '@/app/auth-provider';
import {
  AppShellToolbarTabLink,
  AppShellToolbarTabs,
} from '@/components/core/app-layout/AppShellToolbar';
import { InlineFilterGroup } from '@/components/shared/InlineFilterControls';
import { FilterBar } from '@/components/shared/FilterBar';
import {
  AnalyticsDateRangePicker,
  dateRangeFromPreset,
  type AnalyticsDatePreset,
} from '@/components/shared/AnalyticsDateRangePicker';
import { InlineSpinner } from '@/components/shared/PageLoader';
import { Menu } from '@chakra-ui/react';
import { isWordPressMcpEnabledClient } from '@/lib/wordpress/feature-flags';
import {
  AI_REFERRAL_SHARE_HELP,
  AnalyticsMetricValue,
  AnalyticsTableHeaderLabel,
  AnalyticsPeriodLegend,
  analyticsRowMetric,
  buildAnalyticsRowComparison,
  getAnalyticsComparisonRanges,
  type AnalyticsRowComparison,
} from './AnalyticsDeltaValue';

export type WebsiteTab = 'map' | 'dashboard';
export type WebsiteDateRangeKey = '7d' | '30d' | '90d' | 'custom';

export interface WebsiteDateRange {
  key: WebsiteDateRangeKey;
  label: string;
  startDate: string;
  endDate: string;
}

export interface WebsiteTreeNode {
  name: string;
  path: string;
  pageCount: number;
  avgScore: number;
  grade: string;
  children: WebsiteTreeNode[];
}

export interface WebsiteProjectOption {
  id: string;
  name: string;
  brandDomain?: string | null;
}

export interface WebsiteAnalyticsSource {
  id?: string;
  provider: 'ga4' | 'gsc' | 'clarity' | 'bing_webmaster' | string;
  displayName?: string | null;
  status?: string | null;
  statusDetail?: string | null;
  exposeToMcp?: boolean;
  lastSyncAt?: string | null;
  lastSyncStatus?: string | null;
  lastSyncError?: string | null;
}

export type WebsiteAnalyticsProviderTab = 'ga4' | 'gsc' | 'bing' | 'clarity' | 'cloudflare';
export type WebsiteAnalyticsNavTab =
  | WebsiteTab
  | WebsiteAnalyticsProviderTab
  | 'readiness'
  | 'review';

const WEBSITE_ANALYTICS_TABS: Array<{
  value: WebsiteAnalyticsNavTab;
  label: string;
  icon: typeof Globe2;
}> = [
  { value: 'dashboard', label: 'Overview', icon: BarChart3 },
  { value: 'readiness', label: 'Agent Check', icon: Sparkles },
  { value: 'map', label: 'Pages', icon: Globe2 },
  { value: 'review', label: 'Page Review', icon: FileSearch },
  { value: 'ga4', label: 'GA4', icon: MousePointerClick },
  { value: 'gsc', label: 'GSC', icon: Search },
  { value: 'bing', label: 'Bing', icon: Search },
  { value: 'clarity', label: 'Clarity', icon: Eye },
  { value: 'cloudflare', label: 'Cloudflare', icon: Cloud },
];

function buildWebsiteAnalyticsTabHref({
  tab,
  projectId,
  dateRange,
  selectedPath,
  crawlId,
}: {
  tab: WebsiteAnalyticsNavTab;
  projectId?: string | null;
  dateRange?: WebsiteDateRange | null;
  selectedPath?: string | null;
  crawlId?: string | null;
}) {
  const params = new URLSearchParams();
  if (projectId) params.set('projectId', projectId);
  if (dateRange) {
    params.set('range', dateRange.key);
    if (dateRange.key === 'custom') {
      params.set('start', dateRange.startDate);
      params.set('end', dateRange.endDate);
    }
  }
  const path = (selectedPath || '').trim();
  if (path && path !== '/') params.set('path', path);

  if (tab === 'map') {
    if (crawlId) params.set('crawl', crawlId);
    const query = params.toString();
    return `/app/pages${query ? `?${query}` : ''}`;
  }

  if (tab === 'dashboard') {
    const query = params.toString();
    return `/app/pages/overview${query ? `?${query}` : ''}`;
  }

  if (tab === 'readiness') {
    const query = params.toString();
    return `/app/pages/readiness${query ? `?${query}` : ''}`;
  }

  if (tab === 'review') {
    if (crawlId) params.set('crawlId', crawlId);
    const query = params.toString();
    return `/app/pages/review${query ? `?${query}` : ''}`;
  }

  const query = params.toString();
  return `/app/pages/analytics/${tab}${query ? `?${query}` : ''}`;
}

export function WebsiteAnalyticsTabs({
  activeTab,
  projectId,
  dateRange,
  selectedPath,
  crawlId,
}: {
  activeTab: WebsiteAnalyticsNavTab;
  projectId?: string | null;
  dateRange?: WebsiteDateRange | null;
  selectedPath?: string | null;
  crawlId?: string | null;
}) {
  const pagesHref = buildWebsiteAnalyticsTabHref({
    tab: 'map',
    projectId,
    dateRange,
    selectedPath,
    crawlId,
  });

  return (
    <HStack gap={1.5} minW={0}>
      {activeTab !== 'map' ? (
        <Link
          as={NextLink}
          href={pagesHref}
          aria-label="Back to website pages"
          title="Back to website pages"
          boxSize="36px"
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
          borderWidth="1px"
          borderStyle="solid"
          borderColor="ui.border.subtle"
          borderRadius="10px"
          color="ui.text.secondary"
          textDecoration="none"
          _hover={{
            bg: 'ui.surface.input',
            color: 'ui.text.primary',
            textDecoration: 'none',
          }}
          _focusVisible={{ outline: '2px solid', outlineColor: 'ui.border.focus' }}
        >
          <ChevronLeft size={17} aria-hidden="true" />
        </Link>
      ) : null}
      <AppShellToolbarTabs>
        {WEBSITE_ANALYTICS_TABS.map(tab => {
          const TabIcon = tab.icon;
          return (
            <AppShellToolbarTabLink
              key={tab.value}
              href={buildWebsiteAnalyticsTabHref({
                tab: tab.value,
                projectId,
                dateRange,
                selectedPath,
                crawlId,
              })}
              active={activeTab === tab.value}
              icon={<TabIcon size={16} />}
              label={tab.label}
            >
              {tab.label}
            </AppShellToolbarTabLink>
          );
        })}
      </AppShellToolbarTabs>
    </HStack>
  );
}

interface WebsiteSharedControlsProps {
  projects: WebsiteProjectOption[];
  currentProjectId: string | null;
  onProjectChange: (projectId: string) => void;
  brandDomain: string;
  dateRange: WebsiteDateRange;
  onDateRangeChange: (range: WebsiteDateRange) => void;
  selectedPath: string;
  onPathChange: (path: string) => void;
  siteTree: WebsiteTreeNode | null;
  sources: WebsiteAnalyticsSource[];
  sourcesLoading: boolean;
  showIntegrationControls: boolean;
  variant?: 'page' | 'toolbar';
}

interface WebsiteAnalyticsViewsProps {
  projectId: string | null;
  dateRange: WebsiteDateRange;
  selectedPath: string;
}

export interface WordPressAnalyticsSummary {
  surface: 'wordpress.analytics.summary';
  provider: 'wordpress';
  metricSemantics: 'wordpress_stats';
  resultState: 'ready' | 'not_connected' | 'site_unbound' | 'stats_unavailable' | 'partial';
  site: { id: string; title: string; url: string } | null;
  totals: {
    views: number | null;
    visitors: number | null;
    publishedPosts: number | null;
    publishedPages: number | null;
    subscribers: number | null;
  };
  requestedRange: { startDate: string; endDate: string };
  retrievedAt: string;
  limitations: string[];
}

function AnalyticsSurface({ children, ...props }: React.ComponentProps<typeof Box>) {
  return (
    <Box
      {...props}
      layerStyle="dataTableSurface"
      boxShadow="none"
      borderRadius="0"
      borderWidth={{ base: '1px', md: '0' }}
    >
      {children}
    </Box>
  );
}

interface AnalyticsPageRow {
  normalized_url?: string | null;
  url?: string | null;
  providers?: string[] | null;
  sessions?: number | null;
  active_users?: number | null;
  page_views?: number | null;
  conversions?: number | null;
  revenue?: number | null;
  search_clicks?: number | null;
  search_impressions?: number | null;
  avg_position?: number | null;
}

interface AnalyticsQueryRow {
  query?: string | null;
  sample_url?: string | null;
  providers?: string[] | null;
  clicks?: number | null;
  impressions?: number | null;
  avg_ctr?: number | null;
  avg_position?: number | null;
}

interface PerformanceTrendRow {
  date?: string | null;
  ga4_sessions?: number | null;
  ga4_conversions?: number | null;
  gsc_clicks?: number | null;
  gsc_impressions?: number | null;
  bing_clicks?: number | null;
  bing_impressions?: number | null;
}

interface PerformanceChannelRow {
  channel?: string | null;
  source?: string | null;
  medium?: string | null;
  providers?: string[] | null;
  sessions?: number | null;
  conversions?: number | null;
  revenue?: number | null;
}

interface ChatbotLandingPageRow {
  url?: string | null;
  normalized_url?: string | null;
  page_path?: string | null;
  sessions?: number | null;
  conversions?: number | null;
  revenue?: number | null;
  page_sessions?: number | null;
  chatbot_session_share?: number | null;
  top_sources?: PerformanceChannelRow[];
}

interface AnalyticsUxRow {
  normalized_url?: string | null;
  url?: string | null;
  sessions?: number | null;
  avg_engagement_time_seconds?: number | null;
  avg_scroll_depth?: number | null;
  dead_clicks?: number | null;
  rage_clicks?: number | null;
  excessive_scrolls?: number | null;
  quickbacks?: number | null;
  script_errors?: number | null;
  error_clicks?: number | null;
}

interface AiCrawlerCrawlerRow {
  crawler_operator?: string | null;
  crawler_name?: string | null;
  crawler_category?: string | null;
  requests?: number | null;
  successful_requests?: number | null;
  blocked_requests?: number | null;
  path_count?: number | null;
}

interface AiCrawlerPageRow {
  host?: string | null;
  path?: string | null;
  requests?: number | null;
  successful_requests?: number | null;
  blocked_requests?: number | null;
  crawler_count?: number | null;
}

interface AiCrawlerPayload {
  totals?: Record<string, unknown>;
  crawlerBreakdowns?: {
    crawlers?: AiCrawlerCrawlerRow[];
    pages?: AiCrawlerPageRow[];
  };
}

interface ProviderAnalyticsBlock {
  totals?: Record<string, unknown>;
  pages?: AnalyticsPageRow[];
  queries?: AnalyticsQueryRow[];
  channels?: PerformanceChannelRow[];
  chatbotReferrals?: {
    rows?: PerformanceChannelRow[];
    pathScoped?: boolean;
  };
  chatbotLandingPages?: {
    rows?: ChatbotLandingPageRow[];
  };
  uxSignals?: AnalyticsUxRow[];
}

interface WebsitePerformancePayload {
  providers?: {
    ga4?: ProviderAnalyticsBlock;
    gsc?: ProviderAnalyticsBlock;
    bing?: ProviderAnalyticsBlock;
    clarity?: ProviderAnalyticsBlock;
    cloudflare?: ProviderAnalyticsBlock;
  };
  trend?: PerformanceTrendRow[];
  sourceCoverage?: WebsiteAnalyticsSource[];
}

const EMPTY_PERFORMANCE_TREND: PerformanceTrendRow[] = [];

interface WebsitePerformanceWeekComparison {
  current: WebsitePerformancePayload | null;
  previous: WebsitePerformancePayload | null;
}

interface AiCrawlerWeekComparison {
  current: AiCrawlerPayload | null;
  previous: AiCrawlerPayload | null;
}

export const WEBSITE_DATE_RANGE_PRESETS: readonly AnalyticsDatePreset[] = [
  { key: '7d', label: '7D', days: 7 },
  { key: '30d', label: '30D', days: 30 },
  { key: '90d', label: '90D', days: 90 },
];

const PROVIDERS: Array<{
  key: WebsiteAnalyticsSource['provider'];
  label: string;
  shortLabel: string;
}> = [
  { key: 'ga4', label: 'Google Analytics', shortLabel: 'GA4' },
  { key: 'gsc', label: 'Google Search Console', shortLabel: 'GSC' },
  { key: 'bing_webmaster', label: 'Bing Webmaster Tools', shortLabel: 'Bing' },
  { key: 'clarity', label: 'Microsoft Clarity', shortLabel: 'Clarity' },
  { key: 'cloudflare', label: 'Cloudflare', shortLabel: 'CF' },
];

export function getWebsiteDateRange(key: WebsiteDateRangeKey): WebsiteDateRange {
  const option =
    WEBSITE_DATE_RANGE_PRESETS.find(item => item.key === key) || WEBSITE_DATE_RANGE_PRESETS[1];
  return dateRangeFromPreset(option) as WebsiteDateRange;
}

function numberValue(value: unknown): number {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatCompact(value: unknown): string {
  const number = numberValue(value);
  return new Intl.NumberFormat('en', {
    notation: 'compact',
    maximumFractionDigits: number >= 1000 ? 1 : 0,
  }).format(number);
}

function formatOptionalCompact(value: unknown): string {
  const number = numberValue(value);
  return number ? formatCompact(number) : '—';
}

function formatWordPressMetric(value: number | null | undefined): string {
  return value == null ? 'Unavailable' : formatCompact(value);
}

function formatPercent(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  const number = Number(value);
  if (!Number.isFinite(number)) return '—';
  const pct = number <= 1 ? number * 100 : number;
  return `${pct.toFixed(1)}%`;
}

function formatShare(numerator: unknown, denominator: unknown): string {
  const total = numberValue(denominator);
  return total ? formatPercent(numberValue(numerator) / total) : '—';
}

function formatPosition(value: unknown): string {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number.toFixed(1) : '—';
}

function formatMoney(value: unknown): string {
  const number = numberValue(value);
  return number ? `$${formatCompact(number)}` : '—';
}

const DASHBOARD_TABLE_LIMIT = 5;

function displayUrl(value: string): string {
  try {
    const url = new URL(/^https?:\/\//i.test(value) ? value : `https://${value}`);
    return `${url.hostname.replace(/^www\./, '')}${url.pathname === '/' ? '' : url.pathname}`;
  } catch {
    return value;
  }
}

function crawlerLabel(row: {
  crawler_name?: string | null;
  crawler_operator?: string | null;
  crawler_category?: string | null;
}): string {
  return row.crawler_name || row.crawler_operator || row.crawler_category || 'Unknown crawler';
}

function crawlerDetail(row: {
  crawler_name?: string | null;
  crawler_operator?: string | null;
  crawler_category?: string | null;
}): string {
  const details = [row.crawler_operator, row.crawler_category].filter(Boolean);
  return details.length ? details.join(' | ') : 'Unknown operator';
}

function latestSync(sources: WebsiteAnalyticsSource[]): string | null {
  const times = sources
    .map(source => source.lastSyncAt)
    .filter((value): value is string => Boolean(value))
    .map(value => new Date(value).getTime())
    .filter(Number.isFinite);
  if (times.length === 0) return null;
  return new Date(Math.max(...times)).toISOString();
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return 'Never';
  return new Date(value).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function flattenTree(
  node: WebsiteTreeNode | null
): Array<{ value: string; label: string; pageCount: number }> {
  if (!node) return [{ value: '/', label: '/ (all pages)', pageCount: 0 }];
  const rows: Array<{ value: string; label: string; pageCount: number }> = [];
  const walk = (item: WebsiteTreeNode, depth: number) => {
    rows.push({
      value: item.path,
      label: item.name === '/' ? '/ (all pages)' : `${'  '.repeat(depth)}${item.path}`,
      pageCount: item.pageCount,
    });
    item.children
      .filter(child => child.children.length > 0)
      .slice(0, 80)
      .forEach(child => walk(child, depth + 1));
  };
  walk(node, 0);
  return rows;
}

function sourceTone(sources: WebsiteAnalyticsSource[]): {
  bg: string;
  color: string;
  border: string;
  label: string;
} {
  if (sources.length === 0) {
    return {
      bg: 'ui.surface.selected',
      color: 'ui.text.tertiary',
      border: 'ui.border.subtle',
      label: 'Not connected',
    };
  }
  if (sources.some(source => source.lastSyncStatus === 'failed' || source.status === 'error')) {
    return {
      bg: 'status.danger.bg',
      color: 'status.danger.fg',
      border: 'status.danger.border',
      label: 'Error',
    };
  }
  if (sources.some(source => source.status === 'connected')) {
    return {
      bg: 'status.success.bg',
      color: 'status.success.fg',
      border: 'status.success.border',
      label: 'Connected',
    };
  }
  const status = sources.map(source => source.status).find(Boolean);
  return {
    bg: 'status.warning.bg',
    color: 'status.warning.fg',
    border: 'status.warning.border',
    label: status || 'Needs access',
  };
}

function AnalyticsSourceStatusMenu({
  sourcesByProvider,
  sourcesLoading,
}: {
  sourcesByProvider: Map<string, WebsiteAnalyticsSource[]>;
  sourcesLoading: boolean;
}) {
  return (
    <Menu.Root positioning={{ placement: 'bottom-start', gutter: 8 }}>
      <Menu.Trigger asChild>
        <Box
          as="button"
          layerStyle="filterPill"
          h="30px"
          px={2.5}
          display="inline-flex"
          alignItems="center"
          gap={1.5}
          color="ui.text.secondary"
          fontSize="xs"
          fontWeight="700"
          cursor="pointer"
          _hover={{ bg: 'ui.surface.selected', color: 'ui.text.primary' }}
          _focusVisible={{ outline: 'none', boxShadow: 'none' }}
        >
          <Text>Status</Text>
          <ChevronDown size={10} />
        </Box>
      </Menu.Trigger>
      <Portal>
        <Menu.Positioner>
          <Menu.Content layerStyle="surfacePopover" borderRadius="glassInput" minW="260px" p="1.5">
            {PROVIDERS.map(provider => {
              const providerSources = sourcesByProvider.get(provider.key) || [];
              const tone = sourcesLoading
                ? {
                    bg: 'ui.surface.selected',
                    color: 'ui.text.tertiary',
                    border: 'ui.border.subtle',
                    label: 'Loading',
                  }
                : sourceTone(providerSources);
              return (
                <Box
                  key={provider.key}
                  px="2.5"
                  py="2"
                  borderRadius="glassInput"
                  borderWidth="1px"
                  borderStyle="solid"
                  borderColor={tone.border}
                  bg={tone.bg}
                  color={tone.color}
                >
                  <HStack justify="space-between" gap="3" minW={0}>
                    <Text fontSize="xs" fontWeight="800" color="ui.text.primary" truncate>
                      {provider.label}
                    </Text>
                    <Text fontSize="2xs" fontWeight="800" textTransform="uppercase" flexShrink={0}>
                      {tone.label}
                    </Text>
                  </HStack>
                  <Text fontSize="2xs" color="ui.text.tertiary" mt="0.5">
                    Synced {formatDateTime(latestSync(providerSources))}
                  </Text>
                </Box>
              );
            })}
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
}

function MiniMetric({
  label,
  value,
  align = 'end',
}: {
  label: string;
  value: string;
  align?: 'start' | 'end' | 'center';
}) {
  return (
    <VStack align={align} gap={0} minW={0}>
      <Text fontSize="sm" color="ui.text.primary" fontWeight="700" textAlign={align}>
        {value}
      </Text>
      <Text
        fontSize="10px"
        color="ui.text.tertiary"
        textTransform="uppercase"
        letterSpacing="0.04em"
        textAlign={align}
        truncate
        maxW="100%"
      >
        {label}
      </Text>
    </VStack>
  );
}

function WebsiteSharedControls({
  projects,
  currentProjectId,
  onProjectChange,
  brandDomain,
  dateRange,
  onDateRangeChange,
  selectedPath,
  onPathChange,
  siteTree,
  sources,
  sourcesLoading,
  showIntegrationControls,
}: WebsiteSharedControlsProps) {
  const projectOptions = useMemo(
    () =>
      projects.map(project => ({
        value: project.id,
        label: project.name,
      })),
    [projects]
  );
  const normalizedSelectedPath = selectedPath && selectedPath !== '/' ? selectedPath : '';
  const pathOptions = useMemo(() => {
    const treeOptions = flattenTree(siteTree).map(option => ({
      value: option.value === '/' ? '' : option.value,
      label: `${option.value === '/' ? 'All pages' : option.label.trim()} (${option.pageCount})`,
    }));
    const hasSelectedPathOption = treeOptions.some(
      option => option.value === normalizedSelectedPath
    );

    if (normalizedSelectedPath && !hasSelectedPathOption) {
      return [
        {
          value: normalizedSelectedPath,
          label: `${normalizedSelectedPath} (current)`,
        },
        ...treeOptions,
      ];
    }

    return treeOptions;
  }, [normalizedSelectedPath, siteTree]);
  const sourcesByProvider = useMemo(() => {
    const map = new Map<string, WebsiteAnalyticsSource[]>();
    sources.forEach(source => {
      const providerSources = map.get(source.provider) || [];
      providerSources.push(source);
      map.set(source.provider, providerSources);
    });
    return map;
  }, [sources]);

  return (
    <FilterBar>
      <InlineFilterGroup
        label="Site"
        helpText={brandDomain || undefined}
        options={projectOptions}
        value={currentProjectId || ''}
        onChange={onProjectChange}
        minSelectWidth={{ base: '96px', md: '150px' }}
        hideLabelOnMobile
      />

      <AnalyticsDateRangePicker
        value={dateRange}
        presets={WEBSITE_DATE_RANGE_PRESETS}
        onChange={range => onDateRangeChange(range as WebsiteDateRange)}
        showComparison
        compact
      />

      <InlineFilterGroup
        label="Section"
        options={pathOptions}
        value={normalizedSelectedPath}
        onChange={onPathChange}
        minSelectWidth={{ base: '104px', md: '190px' }}
        hideLabelOnMobile
      />

      {showIntegrationControls ? (
        <Box
          display={{ base: 'none', md: 'contents' }}
          css={{ '@container app-content (max-width: 47.9975rem)': { display: 'none' } }}
        >
          <HStack gap={2} align="center" flexShrink={0}>
            <AnalyticsSourceStatusMenu
              sourcesByProvider={sourcesByProvider}
              sourcesLoading={sourcesLoading}
            />
            <Link
              as={NextLink}
              href="/app/plugins"
              layerStyle="filterPill"
              h="36px"
              px={2.5}
              display="inline-flex"
              alignItems="center"
              gap={1}
              color="status.info.fg"
              fontSize="xs"
              fontWeight="500"
              textDecoration="none"
              _hover={{ textDecoration: 'none', bg: 'ui.surface.selected' }}
            >
              <Text>Manage</Text>
              <ExternalLink size={11} />
            </Link>
          </HStack>
        </Box>
      ) : null}
    </FilterBar>
  );
}

function trendDateLabel(value: unknown): string {
  const raw = String(value || '');
  if (!raw) return '';
  const date = new Date(raw.includes('T') ? raw : `${raw}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function uxIssueCount(row: AnalyticsUxRow | Record<string, unknown>): number {
  return (
    numberValue(row.dead_clicks) +
    numberValue(row.rage_clicks) +
    numberValue(row.excessive_scrolls) +
    numberValue(row.quickbacks) +
    numberValue(row.script_errors) +
    numberValue(row.error_clicks)
  );
}

function providerTotals(block: ProviderAnalyticsBlock | undefined): Record<string, unknown> {
  return block?.totals || {};
}

function pageUrl(row: AnalyticsPageRow | AnalyticsUxRow): string {
  return String(row.url || row.normalized_url || '');
}

function topRows<T>(
  rows: T[] | undefined,
  score: (row: T) => number,
  limit = DASHBOARD_TABLE_LIMIT
): T[] {
  return (rows || [])
    .slice()
    .sort((left, right) => score(right) - score(left))
    .slice(0, limit);
}

function rowWeekMetric<T>(
  comparison: AnalyticsRowComparison<T> | null,
  key: string,
  getValue: (row: T) => unknown
) {
  return comparison ? analyticsRowMetric(comparison, key, getValue) : undefined;
}

function ProviderMetric({
  label,
  value,
  detail,
}: {
  label: string;
  value: string;
  detail?: string;
}) {
  return (
    <VStack align="start" gap={1} minW={0}>
      <Text fontSize="xs" color="ui.text.secondary" fontWeight="800">
        {label}
      </Text>
      <Text fontSize="2xl" color="ui.text.primary" fontWeight="900" lineHeight="1">
        {value}
      </Text>
      {detail && (
        <Text fontSize="xs" color="ui.text.tertiary" truncate maxW="100%">
          {detail}
        </Text>
      )}
    </VStack>
  );
}

function ProviderCard({
  title,
  subtitle,
  icon,
  metrics,
  href,
  visible = true,
}: {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  metrics: Array<{ label: string; value: string; detail?: string }>;
  href: string;
  visible?: boolean;
}) {
  if (!visible) return null;

  const content = (
    <AnalyticsSurface
      p={0}
      overflow="hidden"
      w="full"
      minW={0}
      h="full"
      cursor="pointer"
      transition="border-color 0.15s ease"
      _hover={{ borderColor: 'status.info.border' }}
    >
      <Box
        px={4}
        py={3}
        borderBottomWidth="1px"
        borderBottomStyle="solid"
        borderColor="ui.border.default"
      >
        <HStack justify="space-between" gap={3}>
          <VStack align="start" gap={0} minW={0}>
            <Text fontSize="sm" color="ui.text.primary" fontWeight="900">
              {title}
            </Text>
            <Text fontSize="xs" color="ui.text.tertiary" truncate maxW="100%">
              {subtitle}
            </Text>
          </VStack>
          <HStack color="ui.text.tertiary" gap={2}>
            {icon}
            <ArrowRight size={15} />
          </HStack>
        </HStack>
      </Box>
      <SimpleGrid columns={metrics.length > 2 ? 3 : 2} gap={3} p={4}>
        {metrics.map(metric => (
          <ProviderMetric key={metric.label} {...metric} />
        ))}
      </SimpleGrid>
    </AnalyticsSurface>
  );

  return (
    <Link
      as={NextLink}
      href={href}
      display="block"
      w="full"
      minW={0}
      h="full"
      textDecoration="none"
      _hover={{ textDecoration: 'none' }}
    >
      {content}
    </Link>
  );
}

function WordPressStatsCard({
  summary,
  loading,
  error,
  selectedPath,
}: {
  summary: WordPressAnalyticsSummary | null;
  loading: boolean;
  error: string | null;
  selectedPath: string;
}) {
  const totals = summary?.totals;
  const publishedItems =
    totals?.publishedPosts == null || totals?.publishedPages == null
      ? null
      : totals.publishedPosts + totals.publishedPages;
  const ready = summary?.resultState === 'ready' || summary?.resultState === 'partial';
  const statusLabel = loading
    ? 'Loading live statistics…'
    : error
      ? 'WordPress Stats unavailable'
      : summary?.resultState === 'not_connected'
        ? 'Connect WordPress.com'
        : summary?.resultState === 'site_unbound'
          ? 'Select a primary site'
          : summary?.resultState === 'stats_unavailable'
            ? 'Statistics unavailable'
            : summary?.site?.title || 'Provider-native, site-wide totals';

  return (
    <AnalyticsSurface p={0} overflow="hidden" w="full" minW={0} h="full">
      <Box
        px={4}
        py={3}
        borderBottomWidth="1px"
        borderBottomStyle="solid"
        borderColor="ui.border.default"
      >
        <HStack justify="space-between" gap={3}>
          <VStack align="start" gap={0} minW={0}>
            <Text fontSize="sm" color="ui.text.primary" fontWeight="900">
              Live WordPress Stats
            </Text>
            <Text fontSize="xs" color="ui.text.tertiary" truncate maxW="100%">
              {statusLabel}
            </Text>
          </VStack>
          <Box color={ready ? 'status.success.fg' : 'ui.text.tertiary'} flexShrink={0}>
            {loading ? <InlineSpinner size="xs" /> : <Globe2 size={18} />}
          </Box>
        </HStack>
      </Box>
      <SimpleGrid columns={3} gap={3} p={4}>
        <ProviderMetric label="Views" value={formatWordPressMetric(totals?.views)} />
        <ProviderMetric label="Visitors" value={formatWordPressMetric(totals?.visitors)} />
        <ProviderMetric label="Published" value={formatWordPressMetric(publishedItems)} />
      </SimpleGrid>
      <Box px={4} pb={3}>
        {selectedPath && selectedPath !== '/' ? (
          <Text fontSize="2xs" color="status.warning.fg" fontWeight="700">
            Site-wide; section filter not applied.
          </Text>
        ) : null}
        <HStack mt="1" justify="space-between" gap="2" flexWrap="wrap">
          <Text fontSize="2xs" color="ui.text.tertiary">
            {summary?.retrievedAt
              ? `Retrieved live ${formatDateTime(summary.retrievedAt)}`
              : 'Live request · not synced into Attune totals'}
          </Text>
          {!ready ? (
            <Link
              as={NextLink}
              href="/app/plugins?integration=wordpress"
              fontSize="2xs"
              color="status.info.fg"
              fontWeight="800"
              textDecoration="none"
            >
              Manage connection
            </Link>
          ) : null}
        </HStack>
      </Box>
    </AnalyticsSurface>
  );
}

const TrafficChartPanel = React.memo(function TrafficChartPanel({
  trend,
}: {
  trend: PerformanceTrendRow[];
}) {
  const chartData = trend.map(row => ({
    date: row.date,
    label: trendDateLabel(row.date),
    ga4Sessions: numberValue(row.ga4_sessions),
    gscImpressions: numberValue(row.gsc_impressions),
    bingImpressions: numberValue(row.bing_impressions),
  }));
  const totals = chartData.reduce(
    (acc, row) => ({
      sessions: acc.sessions + row.ga4Sessions,
      gscImpressions: acc.gscImpressions + row.gscImpressions,
      bingImpressions: acc.bingImpressions + row.bingImpressions,
    }),
    { sessions: 0, gscImpressions: 0, bingImpressions: 0 }
  );

  return (
    <AnalyticsSurface p={0} overflow="hidden">
      <Box
        px={4}
        py={3}
        borderBottomWidth="1px"
        borderBottomStyle="solid"
        borderColor="ui.border.default"
      >
        <HStack justify="space-between" gap={3} flexWrap="wrap">
          <Text fontSize="sm" color="ui.text.primary" fontWeight="900">
            Traffic Chart
          </Text>
          <HStack gap={4} flexWrap="wrap">
            <MiniMetric label="GA4 sessions" value={formatOptionalCompact(totals.sessions)} />
            <MiniMetric label="GSC impr." value={formatOptionalCompact(totals.gscImpressions)} />
            <MiniMetric label="Bing impr." value={formatOptionalCompact(totals.bingImpressions)} />
          </HStack>
        </HStack>
      </Box>
      <Box h={{ base: '260px', xl: '300px' }} px={2} py={3}>
        {chartData.length === 0 ? (
          <VStack h="full" justify="center" gap={2}>
            <BarChart3 size={24} color="var(--chakra-colors-ui-text-tertiary)" />
            <Text fontSize="sm" color="ui.text.secondary">
              No analytics trend data in this range.
            </Text>
          </VStack>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
              <CartesianGrid stroke="var(--chakra-colors-ui-border-subtle)" vertical={false} />
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={false}
                tick={{ fill: 'var(--chakra-colors-ui-text-tertiary)', fontSize: 11 }}
                interval="preserveStartEnd"
              />
              <YAxis
                yAxisId="traffic"
                tickLine={false}
                axisLine={false}
                tick={{ fill: 'var(--chakra-colors-ui-text-tertiary)', fontSize: 11 }}
                tickFormatter={formatCompact}
                width={44}
              />
              <YAxis
                yAxisId="search"
                orientation="right"
                tickLine={false}
                axisLine={false}
                tick={{ fill: 'var(--chakra-colors-ui-text-tertiary)', fontSize: 11 }}
                tickFormatter={formatCompact}
                width={44}
              />
              <RechartsTooltip
                formatter={(value: unknown, name: unknown) => [formatCompact(value), String(name)]}
                labelFormatter={(value: unknown) => String(value)}
              />
              <Legend verticalAlign="top" height={28} iconType="plainline" />
              <Line
                yAxisId="traffic"
                type="monotone"
                dataKey="ga4Sessions"
                name="GA4 sessions"
                stroke="var(--chakra-colors-status-success-fg)"
                strokeWidth={2.5}
                dot={false}
              />
              <Line
                yAxisId="search"
                type="monotone"
                dataKey="gscImpressions"
                name="GSC impressions"
                stroke="var(--chakra-colors-status-info-fg)"
                strokeWidth={2.5}
                dot={false}
              />
              <Line
                yAxisId="search"
                type="monotone"
                dataKey="bingImpressions"
                name="Bing impressions"
                stroke="var(--chakra-colors-status-warning-fg)"
                strokeWidth={2.5}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Box>
    </AnalyticsSurface>
  );
});

interface AnalyticsTableColumn<T> {
  key: string;
  label: string;
  helpText?: string;
  align?: 'start' | 'end';
  render: (row: T) => React.ReactNode;
}

function AnalyticsTable<T>({
  title,
  icon,
  rows,
  columns,
  emptyLabel,
  getKey,
  href,
  visible = true,
  showWeekComparison = false,
}: {
  title: string;
  icon: React.ReactNode;
  rows: T[];
  columns: Array<AnalyticsTableColumn<T>>;
  emptyLabel: string;
  getKey: (row: T, index: number) => string;
  href?: string;
  visible?: boolean;
  showWeekComparison?: boolean;
}) {
  if (!visible) return null;

  const card = (
    <AnalyticsSurface p={0} overflow="hidden" h="full">
      <Box
        px={3}
        minH="40px"
        display="flex"
        alignItems="center"
        borderBottomWidth="1px"
        borderBottomStyle="solid"
        borderColor="ui.border.default"
      >
        <HStack justify="space-between" gap={2} w="full">
          <HStack gap={2} minW={0}>
            <Box color="ui.text.tertiary" flexShrink={0} aria-hidden="true">
              {icon}
            </Box>
            <Text fontSize="sm" color="ui.text.primary" fontWeight="750" truncate>
              {title}
            </Text>
          </HStack>
          <HStack gap={2} flexShrink={0}>
            {showWeekComparison ? <AnalyticsPeriodLegend /> : null}
            {href && (
              <Link
                as={NextLink}
                href={href}
                aria-label={`Open ${title} report`}
                display="inline-flex"
                alignItems="center"
                gap={1}
                color="status.info.fg"
                flexShrink={0}
                textDecoration="none"
                _hover={{ color: 'ui.text.primary', textDecoration: 'none' }}
                _focusVisible={{
                  outline: '2px solid',
                  outlineColor: 'status.info.border',
                  outlineOffset: '2px',
                }}
              >
                <Text fontSize="xs" fontWeight="700">
                  Open
                </Text>
                <ArrowRight size={11} aria-hidden="true" />
              </Link>
            )}
          </HStack>
        </HStack>
      </Box>
      <Box overflowX="auto">
        <Table.Root
          size="sm"
          minW="520px"
          tableLayout="fixed"
          bg="transparent"
          css={{
            'thead, tbody, tr, th, td': { backgroundColor: 'inherit' },
          }}
        >
          <Table.Header bg="ui.surface.selected">
            <Table.Row>
              {columns.map((column, index) => (
                <Table.ColumnHeader
                  key={column.key}
                  w={index === 0 ? '36%' : undefined}
                  minW={index === 0 ? '190px' : column.helpText ? '96px' : '82px'}
                  px={3}
                  py={1.5}
                  borderColor="ui.border.subtle"
                  textAlign={column.align === 'end' ? 'right' : 'left'}
                >
                  <AnalyticsTableHeaderLabel
                    label={column.label}
                    helpText={column.helpText}
                    align={column.align}
                  />
                </Table.ColumnHeader>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {rows.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={columns.length} px={3} py={9} textAlign="center">
                  <Text fontSize="sm" color="ui.text.secondary">
                    {emptyLabel}
                  </Text>
                </Table.Cell>
              </Table.Row>
            ) : (
              rows.map((row, index) => (
                <Table.Row
                  key={getKey(row, index)}
                  minH="38px"
                  borderColor="ui.border.subtle"
                  _hover={{ bg: 'ui.surface.selected' }}
                  transition="background-color 120ms ease"
                >
                  {columns.map(column => (
                    <Table.Cell
                      key={column.key}
                      minW={0}
                      px={3}
                      py={1.5}
                      borderColor="ui.border.subtle"
                      textAlign={column.align === 'end' ? 'right' : 'left'}
                    >
                      {column.render(row)}
                    </Table.Cell>
                  ))}
                </Table.Row>
              ))
            )}
          </Table.Body>
        </Table.Root>
      </Box>
    </AnalyticsSurface>
  );

  return card;
}

function PageCell({ url }: { url: string }) {
  return (
    <Text fontSize="14px" color="ui.text.primary" fontWeight="600" truncate maxW="280px">
      {url ? displayUrl(url) : 'Unknown page'}
    </Text>
  );
}

function QueryCell({ query }: { query: string }) {
  return (
    <Text fontSize="14px" color="ui.text.primary" fontWeight="600" truncate>
      {query || 'Unknown query'}
    </Text>
  );
}

function referralLabel(row: PerformanceChannelRow): string {
  const source = String(row.source || '').trim();
  const medium = String(row.medium || '').trim();
  if (source && medium) return `${source} / ${medium}`;
  return source || medium || String(row.channel || '').trim() || 'Unknown source';
}

function CloudflareCrawlerActivitySection({
  aiCrawlers,
  weekComparison,
  href,
}: {
  aiCrawlers: AiCrawlerPayload | null;
  weekComparison?: AiCrawlerWeekComparison;
  href: string;
}) {
  const crawlerBreakdowns = aiCrawlers?.crawlerBreakdowns || {};
  const crawlerRows = topRows(crawlerBreakdowns.crawlers, row => numberValue(row.requests));
  const pageRows = topRows(crawlerBreakdowns.pages, row => numberValue(row.requests));
  const currentBreakdowns = weekComparison?.current?.crawlerBreakdowns;
  const previousBreakdowns = weekComparison?.previous?.crawlerBreakdowns;
  const crawlerWeek = useMemo(
    () =>
      weekComparison
        ? buildAnalyticsRowComparison(
            currentBreakdowns?.crawlers,
            previousBreakdowns?.crawlers,
            crawlerLabel
          )
        : null,
    [currentBreakdowns?.crawlers, previousBreakdowns?.crawlers, weekComparison]
  );
  const pageWeek = useMemo(
    () =>
      weekComparison
        ? buildAnalyticsRowComparison(
            currentBreakdowns?.pages,
            previousBreakdowns?.pages,
            row => `${row.host || ''}${row.path || '/'}`
          )
        : null,
    [currentBreakdowns?.pages, previousBreakdowns?.pages, weekComparison]
  );

  return (
    <SimpleGrid columns={{ base: 1, xl: 2 }} gap={3}>
      <AnalyticsTable
        title="Top Cloudflare AI Crawlers"
        showWeekComparison={Boolean(crawlerWeek)}
        icon={<Bot size={16} />}
        rows={crawlerRows}
        emptyLabel="No Cloudflare crawler breakdown in this range."
        href={href}
        getKey={(row, index) => `${row.crawler_name || row.crawler_operator || 'crawler'}-${index}`}
        columns={[
          {
            key: 'crawler',
            label: 'Crawler',
            render: row => (
              <VStack align="start" gap={1} minW={0}>
                <Text fontSize="sm" color="ui.text.primary" fontWeight="800" truncate maxW="280px">
                  {crawlerLabel(row)}
                </Text>
                <Text fontSize="xs" color="ui.text.tertiary" truncate maxW="280px">
                  {crawlerDetail(row)}
                </Text>
              </VStack>
            ),
          },
          {
            key: 'requests',
            label: 'Req.',
            align: 'end',
            render: row => (
              <AnalyticsMetricValue
                value={formatOptionalCompact(row.requests)}
                comparison={rowWeekMetric(crawlerWeek, crawlerLabel(row), item => item.requests)}
                emphasize
              />
            ),
          },
          {
            key: 'pages',
            label: 'Pages',
            align: 'end',
            render: row => (
              <AnalyticsMetricValue
                value={formatOptionalCompact(row.path_count)}
                comparison={rowWeekMetric(crawlerWeek, crawlerLabel(row), item => item.path_count)}
              />
            ),
          },
          {
            key: 'blocked',
            label: 'Blocked',
            align: 'end',
            render: row => (
              <AnalyticsMetricValue
                value={formatShare(row.blocked_requests, row.requests)}
                comparison={rowWeekMetric(crawlerWeek, crawlerLabel(row), item =>
                  numberValue(item.requests)
                    ? numberValue(item.blocked_requests) / numberValue(item.requests)
                    : 0
                )}
                deltaMode="points"
                lowerIsBetter
              />
            ),
          },
        ]}
      />

      <AnalyticsTable
        title="Crawler-Visited Pages"
        showWeekComparison={Boolean(pageWeek)}
        icon={<Cloud size={16} />}
        rows={pageRows}
        emptyLabel="No Cloudflare crawler page rows in this range."
        href={href}
        getKey={(row, index) => `${row.host || 'host'}-${row.path || 'path'}-${index}`}
        columns={[
          {
            key: 'page',
            label: 'Page',
            render: row => (
              <VStack align="start" gap={1} minW={0}>
                <Text fontSize="sm" color="ui.text.primary" fontWeight="800" truncate maxW="280px">
                  {row.path || '/'}
                </Text>
                <Text fontSize="xs" color="ui.text.tertiary" truncate maxW="280px">
                  {row.host || 'Unknown host'}
                </Text>
              </VStack>
            ),
          },
          {
            key: 'requests',
            label: 'Req.',
            align: 'end',
            render: row => (
              <AnalyticsMetricValue
                value={formatOptionalCompact(row.requests)}
                comparison={rowWeekMetric(
                  pageWeek,
                  `${row.host || ''}${row.path || '/'}`,
                  item => item.requests
                )}
                emphasize
              />
            ),
          },
          {
            key: 'crawlers',
            label: 'Crawlers',
            align: 'end',
            render: row => (
              <AnalyticsMetricValue
                value={formatOptionalCompact(row.crawler_count)}
                comparison={rowWeekMetric(
                  pageWeek,
                  `${row.host || ''}${row.path || '/'}`,
                  item => item.crawler_count
                )}
              />
            ),
          },
          {
            key: 'blocked',
            label: 'Blocked',
            align: 'end',
            render: row => (
              <AnalyticsMetricValue
                value={formatShare(row.blocked_requests, row.requests)}
                comparison={rowWeekMetric(pageWeek, `${row.host || ''}${row.path || '/'}`, item =>
                  numberValue(item.requests)
                    ? numberValue(item.blocked_requests) / numberValue(item.requests)
                    : 0
                )}
                deltaMode="points"
                lowerIsBetter
              />
            ),
          },
        ]}
      />
    </SimpleGrid>
  );
}

function WebsiteProviderDashboard({
  performance,
  weekPerformance,
  aiCrawlers,
  weekAiCrawlers,
  wordpressSummary,
  wordpressLoading,
  wordpressError,
  projectId,
  dateRange,
  selectedPath,
}: {
  performance: WebsitePerformancePayload | null;
  weekPerformance?: WebsitePerformanceWeekComparison;
  aiCrawlers: AiCrawlerPayload | null;
  weekAiCrawlers?: AiCrawlerWeekComparison;
  wordpressSummary: WordPressAnalyticsSummary | null;
  wordpressLoading: boolean;
  wordpressError: string | null;
  projectId: string | null;
  dateRange: WebsiteDateRange;
  selectedPath: string;
}) {
  const wordpressEnabled = isWordPressMcpEnabledClient();
  const showWordPressStats =
    wordpressEnabled &&
    wordpressSummary !== null &&
    wordpressSummary.resultState !== 'not_connected';
  const connectedProviders = new Set(
    (performance?.sourceCoverage || [])
      .filter(source => !source.status || source.status === 'connected')
      .map(source => source.provider)
  );
  const showGa4 = connectedProviders.has('ga4');
  const showGsc = connectedProviders.has('gsc');
  const showBing = connectedProviders.has('bing_webmaster') || connectedProviders.has('bing');
  const showClarity = connectedProviders.has('clarity');
  const showCloudflare = connectedProviders.has('cloudflare');
  const visibleProviderCount = [
    showGa4,
    showGsc,
    showBing,
    showClarity,
    showCloudflare,
    showWordPressStats,
  ].filter(Boolean).length;
  const desktopProviderColumns = visibleProviderCount === 6 ? 3 : Math.max(visibleProviderCount, 1);
  const showTrafficChart = showGa4 || showGsc || showBing || showCloudflare;
  const showProviderTables = showGa4 || showGsc || showBing || showClarity;
  const providers = performance?.providers || {};
  const ga4 = providers.ga4 || {};
  const gsc = providers.gsc || {};
  const bing = providers.bing || {};
  const clarity = providers.clarity || {};
  const cloudflare = providers.cloudflare || {};
  const currentWeekProviders = weekPerformance?.current?.providers;
  const previousWeekProviders = weekPerformance?.previous?.providers;
  const gaTotals = providerTotals(ga4);
  const gscTotals = providerTotals(gsc);
  const bingTotals = providerTotals(bing);
  const clarityTotals = providerTotals(clarity);
  const cloudflareTotals = providerTotals(cloudflare);
  const gaPages = topRows(ga4.pages, row => numberValue(row.sessions));
  const gscQueries = topRows(gsc.queries, row => numberValue(row.impressions));
  const bingQueries = topRows(bing.queries, row => numberValue(row.impressions));
  const clarityRows = topRows(clarity.uxSignals, uxIssueCount);
  const chatbotLandingPages = topRows(ga4.chatbotLandingPages?.rows, row =>
    numberValue(row.sessions)
  );
  const gaPageWeek = useMemo(
    () =>
      weekPerformance
        ? buildAnalyticsRowComparison(
            currentWeekProviders?.ga4?.pages,
            previousWeekProviders?.ga4?.pages,
            pageUrl
          )
        : null,
    [currentWeekProviders?.ga4?.pages, previousWeekProviders?.ga4?.pages, weekPerformance]
  );
  const chatbotLandingPageWeek = useMemo(
    () =>
      weekPerformance
        ? buildAnalyticsRowComparison(
            currentWeekProviders?.ga4?.chatbotLandingPages?.rows,
            previousWeekProviders?.ga4?.chatbotLandingPages?.rows,
            pageUrl
          )
        : null,
    [
      currentWeekProviders?.ga4?.chatbotLandingPages?.rows,
      previousWeekProviders?.ga4?.chatbotLandingPages?.rows,
      weekPerformance,
    ]
  );
  const gscQueryWeek = useMemo(
    () =>
      weekPerformance
        ? buildAnalyticsRowComparison(
            currentWeekProviders?.gsc?.queries,
            previousWeekProviders?.gsc?.queries,
            row => String(row.query || '')
          )
        : null,
    [currentWeekProviders?.gsc?.queries, previousWeekProviders?.gsc?.queries, weekPerformance]
  );
  const bingQueryWeek = useMemo(
    () =>
      weekPerformance
        ? buildAnalyticsRowComparison(
            currentWeekProviders?.bing?.queries,
            previousWeekProviders?.bing?.queries,
            row => String(row.query || '')
          )
        : null,
    [currentWeekProviders?.bing?.queries, previousWeekProviders?.bing?.queries, weekPerformance]
  );
  const clarityWeek = useMemo(
    () =>
      weekPerformance
        ? buildAnalyticsRowComparison(
            currentWeekProviders?.clarity?.uxSignals,
            previousWeekProviders?.clarity?.uxSignals,
            pageUrl
          )
        : null,
    [
      currentWeekProviders?.clarity?.uxSignals,
      previousWeekProviders?.clarity?.uxSignals,
      weekPerformance,
    ]
  );
  const clarityIssues = uxIssueCount(clarityTotals);
  const providerReportHref = (
    provider: 'ga4' | 'gsc' | 'bing' | 'clarity' | 'cloudflare'
  ): string => {
    const params = new URLSearchParams();
    if (projectId) params.set('projectId', projectId);
    params.set('range', dateRange.key);
    if (dateRange.key === 'custom') {
      params.set('start', dateRange.startDate);
      params.set('end', dateRange.endDate);
    }
    const path = (selectedPath || '').trim();
    if (path && path !== '/') params.set('path', path);
    const query = params.toString();
    return `/app/pages/analytics/${provider}${query ? `?${query}` : ''}`;
  };

  if (visibleProviderCount === 0) {
    return (
      <AnalyticsSurface p={{ base: 8, md: 12 }}>
        <VStack gap={3} textAlign="center">
          <Box p={3} borderRadius="full" bg="ui.surface.selected" color="ui.text.tertiary">
            <BarChart3 size={24} />
          </Box>
          <VStack gap={1}>
            <Text color="ui.text.primary" fontWeight="900">
              No analytics sources connected
            </Text>
            <Text maxW="420px" color="ui.text.secondary" fontSize="sm">
              Connect an analytics source to populate this overview.
            </Text>
          </VStack>
          <Link
            as={NextLink}
            href="/app/plugins"
            layerStyle="filterPill"
            h="30px"
            px={3}
            display="inline-flex"
            alignItems="center"
            gap={1}
            color="status.info.fg"
            fontSize="xs"
            fontWeight="800"
            textDecoration="none"
            _hover={{ textDecoration: 'none', bg: 'ui.surface.selected' }}
          >
            <Text>Manage integrations</Text>
            <ExternalLink size={11} />
          </Link>
        </VStack>
      </AnalyticsSurface>
    );
  }

  return (
    <VStack align="stretch" gap={3}>
      <Grid
        display={visibleProviderCount > 0 ? 'grid' : 'none'}
        templateColumns={{
          base: 'minmax(0, 1fr)',
          md: 'repeat(2, minmax(0, 1fr))',
          xl: `repeat(${desktopProviderColumns}, minmax(0, 1fr))`,
        }}
        gap={3}
        alignItems="stretch"
      >
        <ProviderCard
          visible={showGa4}
          title="GA4"
          subtitle="Traffic and outcomes"
          icon={<BarChart3 size={18} />}
          metrics={[
            { label: 'Sessions', value: formatOptionalCompact(gaTotals.sessions) },
            { label: 'Conversions', value: formatOptionalCompact(gaTotals.conversions) },
            { label: 'Revenue', value: formatMoney(gaTotals.revenue) },
          ]}
          href={providerReportHref('ga4')}
        />
        <ProviderCard
          visible={showGsc}
          title="Google Search Console"
          subtitle="Google organic search"
          icon={<Search size={18} />}
          metrics={[
            { label: 'Impressions', value: formatOptionalCompact(gscTotals.search_impressions) },
            { label: 'Clicks', value: formatOptionalCompact(gscTotals.search_clicks) },
            { label: 'CTR', value: formatPercent(gscTotals.search_ctr) },
          ]}
          href={providerReportHref('gsc')}
        />
        <ProviderCard
          visible={showBing}
          title="Bing Webmaster"
          subtitle="Bing organic search"
          icon={<Eye size={18} />}
          metrics={[
            { label: 'Impressions', value: formatOptionalCompact(bingTotals.search_impressions) },
            { label: 'Clicks', value: formatOptionalCompact(bingTotals.search_clicks) },
            { label: 'CTR', value: formatPercent(bingTotals.search_ctr) },
          ]}
          href={providerReportHref('bing')}
        />
        <ProviderCard
          visible={showClarity}
          title="Clarity"
          subtitle="UX friction"
          icon={<MousePointerClick size={18} />}
          metrics={[
            { label: 'Issues', value: formatOptionalCompact(clarityIssues) },
            { label: 'Rage clicks', value: formatOptionalCompact(clarityTotals.rage_clicks) },
            { label: 'Dead clicks', value: formatOptionalCompact(clarityTotals.dead_clicks) },
          ]}
          href={providerReportHref('clarity')}
        />
        <ProviderCard
          visible={showCloudflare}
          title="Cloudflare"
          subtitle="Web Analytics and AI crawlers"
          icon={<Cloud size={18} />}
          metrics={[
            { label: 'Visits', value: formatOptionalCompact(cloudflareTotals.sessions) },
            { label: 'Page views', value: formatOptionalCompact(cloudflareTotals.page_views) },
            {
              label: 'AI crawlers',
              value: formatOptionalCompact(cloudflareTotals.crawler_requests),
            },
          ]}
          href={providerReportHref('cloudflare')}
        />
        {showWordPressStats ? (
          <WordPressStatsCard
            summary={wordpressSummary}
            loading={wordpressLoading}
            error={wordpressError}
            selectedPath={selectedPath}
          />
        ) : null}
      </Grid>

      {showCloudflare ? (
        <CloudflareCrawlerActivitySection
          aiCrawlers={aiCrawlers}
          weekComparison={weekAiCrawlers}
          href={providerReportHref('cloudflare')}
        />
      ) : null}

      {showTrafficChart ? (
        <TrafficChartPanel trend={performance?.trend ?? EMPTY_PERFORMANCE_TREND} />
      ) : null}

      <SimpleGrid
        display={showProviderTables ? 'grid' : 'none'}
        columns={{ base: 1, xl: 2 }}
        gap={3}
      >
        <AnalyticsTable
          visible={showGa4}
          title="Chatbot Landing Pages"
          showWeekComparison={Boolean(chatbotLandingPageWeek)}
          icon={<Bot size={16} />}
          rows={chatbotLandingPages}
          emptyLabel="No page-level chatbot referrals found in GA4 for this range."
          href={providerReportHref('ga4')}
          getKey={(row, index) => pageUrl(row) || `chatbot-page-${index}`}
          columns={[
            { key: 'page', label: 'Page', render: row => <PageCell url={pageUrl(row)} /> },
            {
              key: 'sessions',
              label: 'Sessions',
              align: 'end',
              render: row => (
                <AnalyticsMetricValue
                  value={formatOptionalCompact(row.sessions)}
                  comparison={rowWeekMetric(
                    chatbotLandingPageWeek,
                    pageUrl(row),
                    item => item.sessions
                  )}
                  emphasize
                />
              ),
            },
            {
              key: 'share',
              label: 'AI Share',
              helpText: AI_REFERRAL_SHARE_HELP,
              align: 'end',
              render: row => (
                <AnalyticsMetricValue
                  value={formatPercent(row.chatbot_session_share)}
                  comparison={rowWeekMetric(
                    chatbotLandingPageWeek,
                    pageUrl(row),
                    item => item.chatbot_session_share
                  )}
                  deltaMode="points"
                />
              ),
            },
            {
              key: 'conversions',
              label: 'Conv.',
              align: 'end',
              render: row => (
                <AnalyticsMetricValue
                  value={formatOptionalCompact(row.conversions)}
                  comparison={rowWeekMetric(
                    chatbotLandingPageWeek,
                    pageUrl(row),
                    item => item.conversions
                  )}
                />
              ),
            },
            {
              key: 'revenue',
              label: 'Revenue',
              align: 'end',
              render: row => (
                <AnalyticsMetricValue
                  value={formatMoney(row.revenue)}
                  comparison={rowWeekMetric(
                    chatbotLandingPageWeek,
                    pageUrl(row),
                    item => item.revenue
                  )}
                />
              ),
            },
          ]}
        />
        <AnalyticsTable
          visible={showGa4}
          title="GA4 Top Pages"
          showWeekComparison={Boolean(gaPageWeek)}
          icon={<BarChart3 size={16} />}
          rows={gaPages}
          emptyLabel="No GA4 page data in this range."
          href={providerReportHref('ga4')}
          getKey={(row, index) => pageUrl(row) || `ga4-${index}`}
          columns={[
            { key: 'page', label: 'Page', render: row => <PageCell url={pageUrl(row)} /> },
            {
              key: 'sessions',
              label: 'Sessions',
              align: 'end',
              render: row => (
                <AnalyticsMetricValue
                  value={formatOptionalCompact(row.sessions)}
                  comparison={rowWeekMetric(gaPageWeek, pageUrl(row), item => item.sessions)}
                  emphasize
                />
              ),
            },
            {
              key: 'conversions',
              label: 'Conv.',
              align: 'end',
              render: row => (
                <AnalyticsMetricValue
                  value={formatOptionalCompact(row.conversions)}
                  comparison={rowWeekMetric(gaPageWeek, pageUrl(row), item => item.conversions)}
                />
              ),
            },
            {
              key: 'revenue',
              label: 'Revenue',
              align: 'end',
              render: row => (
                <AnalyticsMetricValue
                  value={formatMoney(row.revenue)}
                  comparison={rowWeekMetric(gaPageWeek, pageUrl(row), item => item.revenue)}
                />
              ),
            },
          ]}
        />
        <AnalyticsTable
          visible={showGsc}
          title="GSC Queries"
          showWeekComparison={Boolean(gscQueryWeek)}
          icon={<Search size={16} />}
          rows={gscQueries}
          emptyLabel="No GSC query data in this range."
          href={providerReportHref('gsc')}
          getKey={(row, index) => String(row.query || `gsc-${index}`)}
          columns={[
            {
              key: 'query',
              label: 'Query',
              render: row => <QueryCell query={String(row.query || '')} />,
            },
            {
              key: 'impressions',
              label: 'Impr.',
              align: 'end',
              render: row => (
                <AnalyticsMetricValue
                  value={formatOptionalCompact(row.impressions)}
                  comparison={rowWeekMetric(
                    gscQueryWeek,
                    String(row.query || ''),
                    item => item.impressions
                  )}
                  emphasize
                />
              ),
            },
            {
              key: 'clicks',
              label: 'Clicks',
              align: 'end',
              render: row => (
                <AnalyticsMetricValue
                  value={formatOptionalCompact(row.clicks)}
                  comparison={rowWeekMetric(
                    gscQueryWeek,
                    String(row.query || ''),
                    item => item.clicks
                  )}
                />
              ),
            },
            {
              key: 'ctr',
              label: 'CTR',
              align: 'end',
              render: row => (
                <AnalyticsMetricValue
                  value={formatPercent(row.avg_ctr)}
                  comparison={rowWeekMetric(
                    gscQueryWeek,
                    String(row.query || ''),
                    item => item.avg_ctr
                  )}
                  deltaMode="points"
                />
              ),
            },
            {
              key: 'position',
              label: 'Pos.',
              align: 'end',
              render: row => (
                <AnalyticsMetricValue
                  value={formatPosition(row.avg_position)}
                  comparison={rowWeekMetric(
                    gscQueryWeek,
                    String(row.query || ''),
                    item => item.avg_position
                  )}
                  deltaMode="absolute"
                  lowerIsBetter
                />
              ),
            },
          ]}
        />
        <AnalyticsTable
          visible={showBing}
          title="Bing Queries"
          showWeekComparison={Boolean(bingQueryWeek)}
          icon={<Eye size={16} />}
          rows={bingQueries}
          emptyLabel="No Bing query data in this range."
          href={providerReportHref('bing')}
          getKey={(row, index) => String(row.query || `bing-${index}`)}
          columns={[
            {
              key: 'query',
              label: 'Query',
              render: row => <QueryCell query={String(row.query || '')} />,
            },
            {
              key: 'impressions',
              label: 'Impr.',
              align: 'end',
              render: row => (
                <AnalyticsMetricValue
                  value={formatOptionalCompact(row.impressions)}
                  comparison={rowWeekMetric(
                    bingQueryWeek,
                    String(row.query || ''),
                    item => item.impressions
                  )}
                  emphasize
                />
              ),
            },
            {
              key: 'clicks',
              label: 'Clicks',
              align: 'end',
              render: row => (
                <AnalyticsMetricValue
                  value={formatOptionalCompact(row.clicks)}
                  comparison={rowWeekMetric(
                    bingQueryWeek,
                    String(row.query || ''),
                    item => item.clicks
                  )}
                />
              ),
            },
            {
              key: 'ctr',
              label: 'CTR',
              align: 'end',
              render: row => (
                <AnalyticsMetricValue
                  value={formatPercent(row.avg_ctr)}
                  comparison={rowWeekMetric(
                    bingQueryWeek,
                    String(row.query || ''),
                    item => item.avg_ctr
                  )}
                  deltaMode="points"
                />
              ),
            },
            {
              key: 'position',
              label: 'Pos.',
              align: 'end',
              render: row => (
                <AnalyticsMetricValue
                  value={formatPosition(row.avg_position)}
                  comparison={rowWeekMetric(
                    bingQueryWeek,
                    String(row.query || ''),
                    item => item.avg_position
                  )}
                  deltaMode="absolute"
                  lowerIsBetter
                />
              ),
            },
          ]}
        />
        <AnalyticsTable
          visible={showClarity}
          title="Clarity Friction"
          showWeekComparison={Boolean(clarityWeek)}
          icon={<MousePointerClick size={16} />}
          rows={clarityRows}
          emptyLabel="No Clarity UX data in this range."
          href={providerReportHref('clarity')}
          getKey={(row, index) => pageUrl(row) || `clarity-${index}`}
          columns={[
            { key: 'page', label: 'Page', render: row => <PageCell url={pageUrl(row)} /> },
            {
              key: 'issues',
              label: 'Issues',
              align: 'end',
              render: row => (
                <AnalyticsMetricValue
                  value={formatOptionalCompact(uxIssueCount(row))}
                  comparison={rowWeekMetric(clarityWeek, pageUrl(row), uxIssueCount)}
                  lowerIsBetter
                  emphasize
                />
              ),
            },
            {
              key: 'rage',
              label: 'Rage',
              align: 'end',
              render: row => (
                <AnalyticsMetricValue
                  value={formatOptionalCompact(row.rage_clicks)}
                  comparison={rowWeekMetric(clarityWeek, pageUrl(row), item => item.rage_clicks)}
                  lowerIsBetter
                />
              ),
            },
            {
              key: 'dead',
              label: 'Dead',
              align: 'end',
              render: row => (
                <AnalyticsMetricValue
                  value={formatOptionalCompact(row.dead_clicks)}
                  comparison={rowWeekMetric(clarityWeek, pageUrl(row), item => item.dead_clicks)}
                  lowerIsBetter
                />
              ),
            },
            {
              key: 'errors',
              label: 'Errors',
              align: 'end',
              render: row => (
                <AnalyticsMetricValue
                  value={formatOptionalCompact(
                    numberValue(row.script_errors) + numberValue(row.error_clicks)
                  )}
                  comparison={rowWeekMetric(
                    clarityWeek,
                    pageUrl(row),
                    item => numberValue(item.script_errors) + numberValue(item.error_clicks)
                  )}
                  lowerIsBetter
                />
              ),
            },
          ]}
        />
      </SimpleGrid>
    </VStack>
  );
}

export function WebsiteAnalyticsViews({
  projectId,
  dateRange,
  selectedPath,
}: WebsiteAnalyticsViewsProps) {
  const { user } = useAuth();
  const wordpressEnabled = isWordPressMcpEnabledClient();
  const [performancePayload, setPerformancePayload] = useState<WebsitePerformancePayload | null>(
    null
  );
  const [weekPerformancePayload, setWeekPerformancePayload] =
    useState<WebsitePerformanceWeekComparison>({
      current: null,
      previous: null,
    });
  const [cloudflareCrawlerPayload, setCloudflareCrawlerPayload] = useState<AiCrawlerPayload | null>(
    null
  );
  const [weekCrawlerPayload, setWeekCrawlerPayload] = useState<AiCrawlerWeekComparison>({
    current: null,
    previous: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [wordpressSummary, setWordpressSummary] = useState<WordPressAnalyticsSummary | null>(null);
  const [wordpressLoading, setWordpressLoading] = useState(false);
  const [wordpressError, setWordpressError] = useState<string | null>(null);

  useEffect(() => {
    if (!user || !projectId) {
      setPerformancePayload(null);
      setWeekPerformancePayload({ current: null, previous: null });
      setCloudflareCrawlerPayload(null);
      setWeekCrawlerPayload({ current: null, previous: null });
      setLoading(false);
      return;
    }
    const currentUser = user;
    const controller = new AbortController();
    async function load() {
      setLoading(true);
      setError(null);
      setCloudflareCrawlerPayload(null);
      setWeekPerformancePayload({ current: null, previous: null });
      setWeekCrawlerPayload({ current: null, previous: null });
      try {
        const token = await currentUser.getIdToken();
        const comparisonRanges = getAnalyticsComparisonRanges(
          dateRange.startDate,
          dateRange.endDate
        );
        const requestBody = {
          projectId,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          path: selectedPath || undefined,
          limit: 25,
        };
        const comparisonBody = {
          projectId,
          path: selectedPath || undefined,
          limit: 25,
        };
        const fetchOptionalPayload = async <T,>(
          url: string,
          body: Record<string, unknown>
        ): Promise<T | null> => {
          try {
            const response = await fetch(url, {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              signal: controller.signal,
              body: JSON.stringify(body),
            });
            const payload = await response.json().catch(() => ({}));
            return response.ok ? (payload as T) : null;
          } catch {
            return null;
          }
        };
        const crawlerBody = {
          ...comparisonBody,
          provider: 'cloudflare',
          includeBreakdowns: true,
        };
        const performanceRequest = fetch('/api/attune/website/performance', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          signal: controller.signal,
          body: JSON.stringify(requestBody),
        });
        const performanceResponse = await performanceRequest;
        const responsePayload = await performanceResponse.json().catch(() => ({}));
        if (!performanceResponse.ok) {
          throw new Error(responsePayload.error || 'Website dashboard could not be loaded.');
        }
        if (controller.signal.aborted) return;
        setPerformancePayload(responsePayload);
        setLoading(false);
        // Load comparisons after the primary dashboard so background queries do
        // not contend with the user-visible request for the connection pool.
        const crawlerRequest = fetchOptionalPayload<AiCrawlerPayload>(
          '/api/attune/analytics/ai-crawlers',
          { ...crawlerBody, startDate: dateRange.startDate, endDate: dateRange.endDate }
        );
        const weekPerformanceRequest = fetchOptionalPayload<WebsitePerformanceWeekComparison>(
          '/api/attune/website/performance-comparison',
          {
            ...comparisonBody,
            current: comparisonRanges.current,
            previous: comparisonRanges.previous,
          }
        );
        const currentWeekCrawlerRequest = fetchOptionalPayload<AiCrawlerPayload>(
          '/api/attune/analytics/ai-crawlers',
          { ...crawlerBody, ...comparisonRanges.current }
        );
        const previousWeekCrawlerRequest = fetchOptionalPayload<AiCrawlerPayload>(
          '/api/attune/analytics/ai-crawlers',
          { ...crawlerBody, ...comparisonRanges.previous }
        );
        void weekPerformanceRequest.then(weekPerformance => {
          if (controller.signal.aborted) return;
          setWeekPerformancePayload(weekPerformance || { current: null, previous: null });
        });
        void Promise.all([
          crawlerRequest,
          currentWeekCrawlerRequest,
          previousWeekCrawlerRequest,
        ]).then(([crawlerPayload, currentWeekCrawlers, previousWeekCrawlers]) => {
          if (controller.signal.aborted) return;
          setCloudflareCrawlerPayload(crawlerPayload);
          setWeekCrawlerPayload({
            current: currentWeekCrawlers,
            previous: previousWeekCrawlers,
          });
        });
      } catch (loadError) {
        if (controller.signal.aborted) return;
        setPerformancePayload(null);
        setWeekPerformancePayload({ current: null, previous: null });
        setCloudflareCrawlerPayload(null);
        setWeekCrawlerPayload({ current: null, previous: null });
        setError(
          loadError instanceof Error ? loadError.message : 'Website dashboard could not be loaded.'
        );
        setLoading(false);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }
    load();
    return () => controller.abort();
  }, [dateRange.endDate, dateRange.startDate, projectId, selectedPath, user]);

  useEffect(() => {
    if (!wordpressEnabled || !user || !projectId) {
      setWordpressSummary(null);
      setWordpressLoading(false);
      setWordpressError(null);
      return;
    }
    const controller = new AbortController();
    const currentUser = user;
    async function loadWordPressStats() {
      setWordpressLoading(true);
      setWordpressError(null);
      try {
        const token = await currentUser.getIdToken();
        const response = await fetch('/api/attune/wordpress/analytics-summary', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          signal: controller.signal,
          body: JSON.stringify({
            projectId,
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
          }),
        });
        const payload = (await response.json().catch(() => ({}))) as WordPressAnalyticsSummary & {
          error?: string;
        };
        if (!response.ok) throw new Error(payload.error || 'WordPress Stats are unavailable.');
        if (controller.signal.aborted) return;
        setWordpressSummary(payload);
      } catch (loadError) {
        if (controller.signal.aborted) return;
        setWordpressSummary(null);
        setWordpressError(
          loadError instanceof Error ? loadError.message : 'WordPress Stats are unavailable.'
        );
      } finally {
        if (!controller.signal.aborted) setWordpressLoading(false);
      }
    }
    void loadWordPressStats();
    return () => controller.abort();
  }, [dateRange.endDate, dateRange.startDate, projectId, user, wordpressEnabled]);

  return (
    <Box minH="560px">
      {loading ? (
        <AnalyticsSurface p={10}>
          <VStack gap={3}>
            <InlineSpinner size="md" />
            <Text color="ui.text.secondary">Loading website dashboard…</Text>
          </VStack>
        </AnalyticsSurface>
      ) : error ? (
        <AnalyticsSurface p={8}>
          <VStack gap={3}>
            <AlertTriangle size={30} color="var(--chakra-colors-status-warning-fg)" />
            <Text fontWeight="700" color="ui.text.primary">
              Dashboard unavailable
            </Text>
            <Text fontSize="sm" color="ui.text.secondary">
              {error}
            </Text>
          </VStack>
        </AnalyticsSurface>
      ) : (
        <WebsiteProviderDashboard
          performance={performancePayload}
          weekPerformance={
            weekPerformancePayload.current && weekPerformancePayload.previous
              ? weekPerformancePayload
              : undefined
          }
          aiCrawlers={cloudflareCrawlerPayload}
          weekAiCrawlers={
            weekCrawlerPayload.current && weekCrawlerPayload.previous
              ? weekCrawlerPayload
              : undefined
          }
          wordpressSummary={wordpressSummary}
          wordpressLoading={wordpressLoading}
          wordpressError={wordpressError}
          projectId={projectId}
          dateRange={dateRange}
          selectedPath={selectedPath}
        />
      )}
    </Box>
  );
}

export { WebsiteSharedControls };
